import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  MdOutlineClose,
  MdOutlineEdit,
  MdOutlineSync,
  MdOutlineSearch,
  MdOutlineUndo,
  MdOutlineViewKanban,
  MdOutlineWhatsapp,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { CRM, STAFF } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import useDebouncedValue from "../hooks/useDebouncedValue";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonKpiStrip,
  SkeletonLine,
} from "../components/crm/Skeletons";
import AccountDrawer from "../components/crm/AccountDrawer";
import {
  formatDaysCount,
  formatMoney,
  formatNumber,
  formatRelativeDays,
  initialsOf,
  toneOf,
  whatsappLink,
} from "../components/crm/crmHelpers";
import ClientLink from "../components/crm/ClientLink";

/*
  Pipeline do ciclo de vida.

  O estágio nasce do uso real da conta, calculado no servidor
  (admin/services/crm/lifecycle.js). O ADMIN pode arrastar o cartão para outra
  coluna e isso muda o estágio de verdade (fila, funil e e-mail automático
  passam a seguir). Para o painel não mentir no dia seguinte, o servidor só
  respeita o arrasto enquanto o estágio calculado continuar o mesmo: quando o
  uso real da conta muda (pagou, parou de agendar), o cartão volta sozinho para
  a coluna calculada. O cartão arrastado leva o selo "movida à mão".

  Arrastar usa o drag and drop nativo do navegador (sem biblioteca), e a tela
  atualiza na hora e desfaz sozinha se o servidor recusar. Não funciona em
  toque (celular/tablet); o painel é de uso em computador.

  O que mudou depois de ver a tela com a base cheia:

  - **Barra de funil no topo.** São nove estágios e cabem quatro na largura da
    tela; sem um resumo proporcional, ninguém enxergava a forma do funil — só
    as quatro primeiras colunas.
  - **Nome em duas linhas.** "[SEED] Studio Lumi…" cortava justamente o
    identificador que a pessoa procura.
  - **Um número por cartão, não três.** Cada estágio tem UMA pergunta
    relevante: em "Novos" é há quanto tempo cadastrou; em "Free ativo" é
    quantos agendamentos fez. Três linhas de rótulo em cinza, repetidas em
    todo cartão, eram 108 repetições das mesmas três palavras numa tela.
  - **Colunas vazias encolhem.** Uma coluna de 280px dizendo "nenhuma conta
    neste estágio" custava o mesmo espaço de uma com trabalho dentro.
*/

const CARD_METRIC = {
  NEW: (card) => ({
    value: formatDaysCount(card.daysSinceRegistration),
    label: "desde o cadastro",
  }),
  ONBOARDING: (card) => ({
    value: formatNumber(card.appointmentsLast30Days),
    label: "agendamentos em 30d",
  }),
  FREE_ACTIVE: (card) => ({
    value: formatNumber(card.appointmentsLast30Days),
    label: "agendamentos em 30d",
  }),
  FREE_IDLE: (card) => ({
    value: formatDaysCount(card.daysSinceLastActivity),
    label: "parado há",
  }),
  FREE_DORMANT: (card) => ({
    value: formatDaysCount(card.daysSinceRegistration),
    label: "desde o cadastro",
  }),
  PAYING: (card) => ({
    value: formatMoney(card.monthlyValue),
    label: "por mês",
  }),
  AT_RISK: (card) => ({
    value: formatRelativeDays(card.planExpirationDate),
    label: "vence",
  }),
  CHURNED: (card) => ({
    value: formatMoney(card.totalPaid),
    label: "pagos até sair",
  }),
};

/*
  Move o cartão no cache do react-query antes de o servidor responder, para o
  arrasto parecer instantâneo. Se o servidor recusar, o `onError` devolve o
  retrato anterior; se aceitar, a nova busca traz a verdade (ordem, contagens).
  Mexe só no que a tela já tem: o cartão que está fora do corte de
  CARDS_PER_STAGE (e por isso não está no cache) não é movido aqui, e a nova
  busca resolve.
*/
const applyMove = (data, cardId, to) => {
  if (!data?.columns) return data;

  const source = data.columns.find((column) =>
    column.cards.some((card) => card.id === cardId)
  );
  const target = data.columns.find((column) => column.id === to);
  if (!source || !target || source.id === target.id) return data;

  const card = source.cards.find((item) => item.id === cardId);
  const moved = { ...card, stage: to, stageIsManual: to !== card.derivedStage };
  const money = Number(card.monthlyValue) || 0;
  const neverContacted = card.lastContactAt ? 0 : 1;
  const withTasks = card.openTasks > 0 ? 1 : 0;

  return {
    ...data,
    columns: data.columns.map((column) => {
      if (column.id === source.id) {
        return {
          ...column,
          count: column.count - 1,
          mrr: column.mrr - money,
          neverContacted: column.neverContacted - neverContacted,
          withOpenTasks: column.withOpenTasks - withTasks,
          cards: column.cards.filter((item) => item.id !== cardId),
        };
      }

      if (column.id === target.id) {
        return {
          ...column,
          count: column.count + 1,
          mrr: column.mrr + money,
          neverContacted: column.neverContacted + neverContacted,
          withOpenTasks: column.withOpenTasks + withTasks,
          // O servidor põe o cartão movido à mão no topo; aqui também.
          cards: [moved, ...column.cards],
        };
      }

      return column;
    }),
  };
};

const ClientCard = ({
  card,
  stage,
  isActive,
  onOpen,
  canMove,
  isDragging,
  onDragStart,
  onDragEnd,
  derivedLabel,
}) => {
  const phone = whatsappLink(card.phoneNumber);
  const metric = (CARD_METRIC[stage] || CARD_METRIC.NEW)(card);

  return (
    <div
      draggable={canMove}
      onDragStart={canMove ? (event) => onDragStart(event, card, stage) : undefined}
      onDragEnd={canMove ? onDragEnd : undefined}
      className={`rounded-xl border bg-white p-2.5 shadow-sm transition ${
        canMove ? "cursor-grab active:cursor-grabbing" : ""
      } ${isDragging ? "opacity-40" : ""} ${
        isActive
          ? "border-pink-300 ring-2 ring-pink-100"
          : "border-slate-200 hover:border-pink-200 hover:shadow"
      }`}
    >
      {/*
        div com role="button" e não <button>: o Firefox não deixa arrastar um
        elemento cuja área clicável é um <button> dentro dele.
      */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(card.id)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen(card.id);
          }
        }}
        className="flex w-full items-start gap-2 text-left"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-[10px] font-bold text-pink-700">
          {initialsOf(card.name)}
        </span>
        <span className="min-w-0 flex-1">
          {/* Duas linhas: o nome do salão é o que a pessoa procura. */}
          <span className="line-clamp-2 text-[13px] font-bold leading-tight text-slate-900">
            {card.name}
          </span>
        </span>
      </div>

      {card.stageIsManual ? (
        <span
          title={`O uso real indica "${derivedLabel}". Se o uso da conta mudar, ela volta para lá sozinha.`}
          className="mt-1.5 inline-flex rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
        >
          movida à mão
        </span>
      ) : null}

      <div className="mt-2 flex items-end justify-between gap-2">
        <span>
          <span className="block text-base font-bold leading-none text-slate-900">
            {metric.value}
          </span>
          <span className="block text-[10px] leading-tight text-slate-400">
            {metric.label}
          </span>
        </span>

        <span className="flex items-center gap-1">
          {card.openTasks > 0 ? (
            <Badge tone="info">{card.openTasks}</Badge>
          ) : null}
          {phone ? (
            <a
              href={phone}
              draggable={false}
              target="_blank"
              rel="noreferrer"
              title="Abrir WhatsApp"
              className="rounded-md p-1 text-slate-300 transition hover:bg-emerald-50 hover:text-emerald-600"
            >
              <MdOutlineWhatsapp size={15} />
            </a>
          ) : null}
        </span>
      </div>

      {/*
        O contato é a informação que decide a ordem de trabalho, então ganha
        uma linha própria. "nunca" em vermelho só quando é de fato o caso —
        pintar todos de vermelho (como na primeira versão) vira ruído e
        ninguém mais olha.
      */}
      <p
        className={`mt-1.5 border-t border-slate-100 pt-1.5 text-[10px] ${
          card.lastContactAt ? "text-slate-500" : "font-semibold text-rose-600"
        }`}
      >
        {card.lastContactAt
          ? `falado ${formatRelativeDays(card.lastContactAt)}`
          : "nunca contatada"}
        {card.ownerName ? ` · ${card.ownerName}` : ""}
      </p>
    </div>
  );
};

const Column = ({ column, openId, onOpen, dnd, stageLabelOf }) => {
  const tone = toneOf(column.tone);

  // Só quem pode mover recebe soltar; o cartão só vira alvo de outra coluna.
  const dropProps = dnd.canMove
    ? {
        onDragOver: (event) => dnd.onColumnOver(column.id, event),
        onDragLeave: (event) => dnd.onColumnLeave(column.id, event),
        onDrop: (event) => dnd.onColumnDrop(column.id, event),
      }
    : {};
  const isOver =
    Boolean(dnd.dragging) &&
    dnd.overStage === column.id &&
    dnd.dragging.from !== column.id;

  // Coluna vazia vira uma faixa estreita: continua visível (o estágio existe
  // e está zerado, que é informação) sem custar a largura de uma com trabalho.
  // Enquanto alguém arrasta um cartão ela abre, porque é um destino possível
  // e precisa de área para soltar e de nome legível.
  if (column.count === 0 && dnd.canMove && dnd.dragging) {
    return (
      <div
        {...dropProps}
        className={`flex w-[232px] shrink-0 flex-col items-center justify-center rounded-2xl border-2 border-dashed px-3 text-center transition ${
          isOver
            ? "border-pink-400 bg-pink-50"
            : "border-slate-300 bg-slate-50"
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
        <p className="mt-2 text-[13px] font-bold text-slate-700">
          {column.label}
        </p>
        <p className="mt-0.5 text-[11px] text-slate-500">Soltar aqui</p>
      </div>
    );
  }

  if (column.count === 0) {
    return (
      <div className="flex w-[52px] shrink-0 flex-col items-center rounded-2xl bg-slate-50 py-3">
        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
        <span
          className="mt-2 whitespace-nowrap text-[11px] font-semibold text-slate-400"
          style={{ writingMode: "vertical-rl" }}
        >
          {column.label} · 0
        </span>
      </div>
    );
  }

  return (
    <div
      {...dropProps}
      className={`flex w-[232px] shrink-0 flex-col rounded-2xl p-2.5 transition ${
        isOver ? "bg-pink-50 ring-2 ring-pink-300" : "bg-slate-50"
      }`}
    >
      <div className="mb-2 px-0.5">
        <div className="flex items-center justify-between gap-2">
          <h3
            className="truncate text-[13px] font-bold text-slate-900"
            title={column.description}
          >
            {column.label}
          </h3>
          <Badge tone={column.tone}>{formatNumber(column.count)}</Badge>
        </div>

        <p className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-slate-500">
          {column.mrr > 0 ? (
            <span className="font-semibold text-slate-700">
              {formatMoney(column.mrr)}/mês
            </span>
          ) : null}
          {column.neverContacted > 0 ? (
            <span className="text-rose-600">
              {column.neverContacted} sem contato
            </span>
          ) : null}
        </p>
      </div>

      <div className="flex-1 space-y-1.5 overflow-y-auto pr-0.5">
        {column.cards.map((card) => (
          <ClientCard
            key={card.id}
            card={card}
            stage={column.id}
            isActive={openId === card.id}
            onOpen={onOpen}
            canMove={dnd.canMove}
            isDragging={dnd.dragging?.id === card.id}
            onDragStart={dnd.onCardDragStart}
            onDragEnd={dnd.onCardDragEnd}
            derivedLabel={stageLabelOf(card.derivedStage)}
          />
        ))}

        {column.hasMore ? (
          <ClientLink
            hideWhenBlocked
            to={`/clientes?stage=${column.id}`}
            className="block rounded-xl border border-dashed border-slate-300 py-2 text-center text-[10px] font-semibold text-slate-500 transition hover:border-pink-300 hover:text-pink-700"
          >
            ver as {formatNumber(column.count)} na tabela
          </ClientLink>
        ) : null}
      </div>
    </div>
  );
};

/*
  Resumo proporcional do funil. É a leitura que o kanban não dá: com nove
  colunas e quatro visíveis, a forma da base ficava escondida atrás da rolagem
  horizontal. Clicar num segmento rola até a coluna.
*/
const FunnelBar = ({ columns, total }) => (
  <div>
    <div className="flex h-7 w-full overflow-hidden rounded-xl">
      {columns
        .filter((column) => column.count > 0)
        .map((column) => (
          <button
            key={column.id}
            type="button"
            title={`${column.label}: ${column.count} (${column.description})`}
            onClick={() =>
              document
                .getElementById(`col-${column.id}`)
                ?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" })
            }
            className={`group relative flex items-center justify-center transition hover:brightness-95 ${
              toneOf(column.tone).bar
            }`}
            style={{ width: `${(column.count / Math.max(total, 1)) * 100}%` }}
          >
            <span className="px-1 text-[10px] font-bold text-white/90">
              {column.count}
            </span>
          </button>
        ))}
    </div>

    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
      {columns
        .filter((column) => column.count > 0)
        .map((column) => (
          <span
            key={column.id}
            className="flex items-center gap-1 text-[11px] text-slate-600"
          >
            <span
              className={`h-2 w-2 rounded-full ${toneOf(column.tone).dot}`}
            />
            {column.label}
          </span>
        ))}
    </div>
  </div>
);

/*
  Renomear as etapas (só admin). Muda apenas o NOME mostrado: a regra que
  decide em que etapa cada conta cai continua a mesma. O nome vale para o
  pipeline, o funil, as filas, a tabela de clientes e os e-mails automáticos.
  Campo vazio ou "Restaurar padrão" volta ao nome original.
*/
const MAX_STAGE_LABEL = 40;

const StageRenamePanel = ({ stages, onClose }) => {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [savedId, setSavedId] = useState(null);

  const save = useMutation(
    async ({ id, label }) => {
      const { data } = await axios.put(`${CRM.STAGES}/${id}`, { label });
      return data.data;
    },
    {
      onMutate: ({ id }) => {
        setSavingId(id);
        setErrors((prev) => ({ ...prev, [id]: null }));
      },
      onSuccess: (_result, { id }) => {
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setSavedId(id);
        setTimeout(() => setSavedId((current) => (current === id ? null : current)), 2500);
        // Pipeline, funil, filas e a tabela de clientes leem o mesmo nome.
        queryClient.invalidateQueries(["crm"]);
        queryClient.invalidateQueries(["companies"]);
      },
      onError: (error, { id }) => {
        setErrors((prev) => ({
          ...prev,
          [id]:
            error?.response?.data?.message ||
            "Não foi possível salvar o nome. Tente de novo.",
        }));
      },
      onSettled: () => setSavingId(null),
    }
  );

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-slate-900">Nome das etapas</h2>
          <p className="text-xs text-slate-500">
            Muda só o nome mostrado, em todas as telas e nos e-mails. A regra
            que coloca cada conta numa etapa continua a mesma.
          </p>
        </div>
        <button
          type="button"
          aria-label="Fechar"
          onClick={onClose}
          className="rounded-md p-1 text-slate-400 transition hover:text-slate-700"
        >
          <MdOutlineClose size={16} />
        </button>
      </div>

      <ul className="grid gap-2 sm:grid-cols-2">
        {stages.map((stage) => {
          const value = drafts[stage.id] ?? stage.label;
          const changed = value.trim() !== stage.label;
          const saving = savingId === stage.id;

          return (
            <li key={stage.id} className="rounded-xl bg-slate-50 p-2.5">
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${toneOf(stage.tone).dot}`}
                />
                <form
                  className="flex min-w-0 flex-1 items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (changed && !saving) {
                      save.mutate({ id: stage.id, label: value });
                    }
                  }}
                >
                  <input
                    value={value}
                    maxLength={MAX_STAGE_LABEL}
                    aria-label={`Nome da etapa ${stage.defaultLabel}`}
                    onChange={(event) =>
                      setDrafts((prev) => ({ ...prev, [stage.id]: event.target.value }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        setDrafts((prev) => {
                          const next = { ...prev };
                          delete next[stage.id];
                          return next;
                        });
                      }
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                  />
                  <button
                    type="submit"
                    disabled={!changed || saving}
                    className="rounded-lg bg-pink-600 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {saving ? "Salvando..." : "Salvar"}
                  </button>
                </form>
              </div>

              <p className="mt-1 pl-4 text-[11px] text-slate-500">
                {stage.description}
              </p>

              <div className="mt-1 flex flex-wrap items-center gap-x-3 pl-4 text-[11px]">
                {stage.customized ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => save.mutate({ id: stage.id, label: "" })}
                    className="font-semibold text-slate-600 underline-offset-2 hover:underline disabled:opacity-50"
                  >
                    Restaurar padrão ({stage.defaultLabel})
                  </button>
                ) : null}
                {savedId === stage.id ? (
                  <span className="font-semibold text-emerald-700">Nome salvo</span>
                ) : null}
                {errors[stage.id] ? (
                  <span className="font-semibold text-rose-700">{errors[stage.id]}</span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const PipelinePage = () => {
  const queryClient = useQueryClient();
  const { isAdmin, isAdminOrOperator } = useApplication();
  const [search, setSearch] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [openId, setOpenId] = useState(null);
  // Cartão em arrasto ({ id, name, from }), coluna sob o cursor e o aviso do
  // último movimento (sucesso com "Desfazer", ou erro).
  const [dragging, setDragging] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const boardRef = useRef(null);
  const debouncedSearch = useDebouncedValue(search, 400);

  /*
    Rolagem lateral durante o arrasto. São 8 colunas e só cabem 4 a 5 na
    janela; sem isso não dá para chegar em "Pagante" ou "Perdido" com o cartão
    na mão. Não dá para depender da rolagem automática do navegador (o Chrome
    não rola o quadro sozinho em todos os casos). Perto da borda do quadro, a
    velocidade cresce com a proximidade; fora das bordas não rola nada.
  */
  useEffect(() => {
    if (!dragging) return undefined;

    const EDGE = 110;
    const MAX_SPEED = 24;
    let pointerX = null;
    let frame = null;

    const track = (event) => {
      pointerX = event.clientX;
    };

    const tick = () => {
      const board = boardRef.current;
      if (board && pointerX !== null) {
        const rect = board.getBoundingClientRect();
        const toRight = rect.right - pointerX;
        const toLeft = pointerX - rect.left;

        if (toRight < EDGE) {
          board.scrollLeft += Math.ceil(
            ((EDGE - Math.max(toRight, 0)) / EDGE) * MAX_SPEED
          );
        } else if (toLeft < EDGE) {
          board.scrollLeft -= Math.ceil(
            ((EDGE - Math.max(toLeft, 0)) / EDGE) * MAX_SPEED
          );
        }
      }
      frame = requestAnimationFrame(tick);
    };

    document.addEventListener("dragover", track);
    frame = requestAnimationFrame(tick);

    return () => {
      document.removeEventListener("dragover", track);
      cancelAnimationFrame(frame);
    };
  }, [dragging]);

  // O aviso de sucesso some sozinho; o de erro fica até alguém fechar.
  useEffect(() => {
    if (feedback?.kind !== "success") return undefined;
    const timer = setTimeout(() => setFeedback(null), 10000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const { data: owners } = useQuery(
    ["staff", "options"],
    async () => {
      const { data } = await axios.get(STAFF.OPTIONS);
      return data.data;
    },
    { enabled: isAdminOrOperator }
  );

  const pipelineQuery = useQuery(
    ["crm", "pipeline", debouncedSearch, ownerUserId],
    async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (ownerUserId) params.set("ownerUserId", ownerUserId);

      const { data } = await axios.get(`${CRM.PIPELINE}?${params.toString()}`);
      return data.data;
    },
    { keepPreviousData: true }
  );

  const syncStages = useMutation(
    async () => {
      const { data } = await axios.post(CRM.PIPELINE_SYNC);
      return data.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["crm"]),
    }
  );

  const data = pipelineQuery.data;
  const canMove = isAdminOrOperator;

  const stageLabelOf = (stageId) =>
    (data?.stages || []).find((item) => item.id === stageId)?.label || stageId;

  const moveCard = useMutation(
    async ({ cardId, to }) => {
      const { data: response } = await axios.put(
        `${CRM.CLIENT_STAGE}/${cardId}/stage`,
        { stage: to }
      );
      return response.data;
    },
    {
      onMutate: async ({ cardId, to }) => {
        await queryClient.cancelQueries(["crm", "pipeline"]);
        const snapshots = queryClient.getQueriesData(["crm", "pipeline"]);
        queryClient.setQueriesData(["crm", "pipeline"], (old) =>
          applyMove(old, cardId, to)
        );
        return { snapshots };
      },
      onError: (error, variables, context) => {
        (context?.snapshots || []).forEach(([key, snapshot]) =>
          queryClient.setQueryData(key, snapshot)
        );
        setFeedback({
          kind: "error",
          text:
            error?.response?.data?.message ||
            "Não foi possível mover a conta. Tente de novo.",
        });
      },
      onSuccess: (result, variables) => {
        if (!result.changed) return;

        setFeedback({
          kind: "success",
          text: variables.isUndo
            ? `Conta ${result.name} voltou para ${stageLabelOf(result.stage)}.`
            : `Conta ${result.name} movida para ${stageLabelOf(result.stage)}.`,
          // Desfazer leva a conta de volta ao estágio em que ela estava. Não
          // se desfaz um desfazer: o aviso seguinte já não oferece o botão.
          undo: variables.isUndo
            ? null
            : { cardId: result.companyId, to: result.previousStage },
        });
      },
      onSettled: () => {
        // Fila, funil e tabela de clientes leem o mesmo estágio.
        queryClient.invalidateQueries(["crm"]);
        queryClient.invalidateQueries(["companies"]);
      },
    }
  );

  const clearDrag = () => {
    setDragging(null);
    setOverStage(null);
  };

  const dnd = {
    canMove,
    dragging,
    overStage,
    onCardDragStart: (event, card, from) => {
      event.dataTransfer.effectAllowed = "move";
      // O Firefox só inicia o arrasto se houver algum dado.
      event.dataTransfer.setData("text/plain", String(card.id));
      // Um tick depois: mexer no layout dentro do dragstart (as colunas vazias
      // abrem) cancela o arrasto no Chrome.
      setTimeout(() => setDragging({ id: card.id, name: card.name, from }), 0);
    },
    onCardDragEnd: clearDrag,
    onColumnOver: (columnId, event) => {
      if (!dragging) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setOverStage(columnId);
    },
    onColumnLeave: (columnId, event) => {
      // Passar para um filho da própria coluna também dispara dragleave.
      if (event.currentTarget.contains(event.relatedTarget)) return;
      setOverStage((current) => (current === columnId ? null : current));
    },
    onColumnDrop: (columnId, event) => {
      if (!dragging) return;
      event.preventDefault();
      const { id, from } = dragging;
      clearDrag();
      if (columnId === from) return;

      setFeedback(null);
      moveCard.mutate({ cardId: id, to: columnId });
    },
  };

  // Navegação do painel percorre os cartões na ordem visual das colunas.
  const flatCards = useMemo(
    () => (data?.columns || []).flatMap((column) => column.cards),
    [data]
  );
  const openIndex = flatCards.findIndex((card) => card.id === openId);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Pipeline</h1>
          <p className="text-sm text-slate-500">
            {canMove
              ? "Arraste um cartão para mudar o estágio da conta. Se o uso real mudar depois, ela volta sozinha para a coluna calculada."
              : "Estágio calculado pelo uso real da conta."}
            {data ? (
              <>
                {" "}
                <span className="font-semibold text-slate-700">
                  {formatNumber(data.totals.companies)} contas
                </span>
                , {formatNumber(data.totals.neverContacted)} nunca contatadas.
              </>
            ) : null}
          </p>
        </div>

        {isAdminOrOperator ? (
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin ? (
              <button
                type="button"
                onClick={() => setRenaming((open) => !open)}
                aria-expanded={renaming}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-pink-200"
              >
                <MdOutlineEdit size={16} />
                Renomear etapas
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => syncStages.mutate()}
              disabled={syncStages.isLoading}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-pink-200 disabled:opacity-50"
            >
              <MdOutlineSync
                size={16}
                className={syncStages.isLoading ? "animate-spin" : ""}
              />
              {syncStages.isLoading ? "Reclassificando..." : "Reclassificar base"}
            </button>
          </div>
        ) : null}
      </header>

      {isAdmin && renaming && data?.stages ? (
        <StageRenamePanel stages={data.stages} onClose={() => setRenaming(false)} />
      ) : null}

      {syncStages.isSuccess && syncStages.data ? (
        <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
          {formatNumber(syncStages.data.checked)} contas verificadas,{" "}
          {formatNumber(syncStages.data.moved)} mudaram de estágio.
        </p>
      ) : null}

      {feedback ? (
        <div
          role="status"
          className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-xs font-semibold ring-1 ring-inset ${
            feedback.kind === "error"
              ? "bg-rose-50 text-rose-800 ring-rose-200"
              : "bg-emerald-50 text-emerald-800 ring-emerald-200"
          }`}
        >
          <span>{feedback.text}</span>
          <span className="flex shrink-0 items-center gap-2">
            {feedback.undo ? (
              <button
                type="button"
                onClick={() => {
                  const { cardId, to } = feedback.undo;
                  setFeedback(null);
                  moveCard.mutate({ cardId, to, isUndo: true });
                }}
                className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-emerald-800 ring-1 ring-inset ring-emerald-200 transition hover:bg-emerald-100"
              >
                <MdOutlineUndo size={14} />
                Desfazer
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Fechar aviso"
              onClick={() => setFeedback(null)}
              className="rounded-md p-0.5 opacity-70 transition hover:opacity-100"
            >
              <MdOutlineClose size={14} />
            </button>
          </span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <MdOutlineSearch
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome, e-mail, telefone, documento ou #id"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm shadow-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
          />
        </div>

        {isAdminOrOperator ? (
          <select
            value={ownerUserId}
            onChange={(event) => setOwnerUserId(event.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-pink-300"
          >
            <option value="">Toda a base</option>
            <option value="none">Sem responsável</option>
            {(owners || []).map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {pipelineQuery.error ? (
        <ErrorBlock
          message="Não foi possível carregar o pipeline."
          onRetry={pipelineQuery.refetch}
        />
      ) : pipelineQuery.isLoading ? (
        <>
          <SkeletonKpiStrip count={3} withIcon={false} />
          <div className="flex gap-2.5 overflow-hidden">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={`col-skeleton-${index}`}
                className="w-[232px] shrink-0 space-y-2 rounded-2xl bg-slate-50 p-2.5"
              >
                <SkeletonLine className="h-3 w-28" />
                {Array.from({ length: 4 }).map((__, card) => (
                  <div
                    key={`card-${index}-${card}`}
                    className="space-y-2 rounded-xl border border-slate-200 bg-white p-2.5"
                  >
                    <SkeletonLine className="h-3 w-36" />
                    <SkeletonLine className="h-4 w-16" />
                    <SkeletonLine className="h-2 w-28" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      ) : data?.totals.companies === 0 ? (
        <Card>
          <EmptyState
            icon={MdOutlineViewKanban}
            title="Nenhuma conta no recorte"
            description="Limpe a busca ou troque o responsável selecionado."
          />
        </Card>
      ) : (
        <>
          <FunnelBar columns={data.columns} total={data.totals.companies} />

          <div
            ref={boardRef}
            className="flex h-[calc(100vh-20rem)] min-h-[420px] gap-2.5 overflow-x-auto pb-2"
          >
            {data.columns.map((column) => (
              <div key={column.id} id={`col-${column.id}`} className="flex">
                <Column
                  column={column}
                  openId={openId}
                  onOpen={setOpenId}
                  dnd={dnd}
                  stageLabelOf={stageLabelOf}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {openId ? (
        <AccountDrawer
          companyId={openId}
          onClose={() => setOpenId(null)}
          position={openIndex >= 0 ? openIndex + 1 : null}
          total={flatCards.length}
          onPrev={
            openIndex > 0
              ? () => setOpenId(flatCards[openIndex - 1].id)
              : undefined
          }
          onNext={
            openIndex >= 0 && openIndex < flatCards.length - 1
              ? () => setOpenId(flatCards[openIndex + 1].id)
              : undefined
          }
        />
      ) : null}
    </div>
  );
};

export default PipelinePage;
